import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { BASE_URL } from "../../config";
import { getAuthHeaders } from "../../services/useAuth";
import CustomButton from "../../pages/CustomButton";
import Loader from "../../dist/Loader";
import { toast } from "react-toastify";
import DailyPunchedAttendance from "../../css/DailyPunchedAttendence.css";

const ProductCampaignDailyReport = () => {
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEmployees = useCallback(async (stateId, divisionName) => {
    if (!stateId) {
      setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
      return;
    }
    try {
      const params = new URLSearchParams();
      params.set("state_id", stateId);
      if (divisionName && divisionName !== "All") {
        params.set("division", divisionName);
      }

      const res = await axios.get(
        `${BASE_URL}/api/employees?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setEmployees([{ id: "All", first_name: "All", last_name: "" }, ...list]);
      setSelectedEmployee("All");
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
    }
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        const list = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];
        setStates(list);
      } catch (err) {
        console.error("Error fetching states:", err);
        setError("Failed to load states");
      }
    };
    fetchStates();
  }, []);

  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");
    setSelectedEmployee("All");
    setTableData([]);
    setShowContainers(false);

    if (!stateId) {
      setDivisions([{ id: "All", name: "All" }]);
      fetchEmployees("", "All");
      return;
    }

    try {
      const res = await axios.get(
        `${BASE_URL}/api/divisions?state_id=${stateId}`,
        { headers: getAuthHeaders() }
      );
      const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];
      const list = raw.map((d) =>
        typeof d === "string" ? { id: d, name: d } : d
      );
      setDivisions([{ id: "All", name: "All" }, ...list]);
    } catch (err) {
      console.error("Error loading divisions", err);
      setDivisions([{ id: "All", name: "All" }]);
    }

    fetchEmployees(stateId, "All");
  };

  const handleDivisionChange = async (value) => {
    setSelectedDivision(value);
    setSelectedEmployee("All");
    fetchEmployees(selectedState, value);
  };

  const handleGoClick = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setShowContainers(false);
      setTableData([]);

      const params = new URLSearchParams();
      if (selectedState) params.set("state_id", selectedState);
      if (selectedDivision && selectedDivision !== "All") {
        params.set("division", selectedDivision);
      }
      if (selectedEmployee && selectedEmployee !== "All") {
        // ensure we pass employee as employee_id in query
        params.set("employee_id", selectedEmployee);
      }
      params.set("selected_date", format(selectedDate, "yyyy-MM-dd"));

      const url = `${BASE_URL}/api/reports/employee-daily-temptation?${params.toString()}`;

      const response = await axios.get(url, { headers: getAuthHeaders() });

      const rows = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      setTableData(rows);
      setShowContainers(true);
    } catch (err) {
      console.error("Error fetching report:", err);
      setError("Could not load report. Please try again.");
      setTableData([]);
      setShowContainers(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Column definitions with label and extractor fallback
  const columns = React.useMemo(() => {
    return [
      {
        key: "sno",
        label: "S.No",
        extract: (_row, index) => index + 1,
      },
      {
        key: "empId",
        label: "Emp ID",
        extract: (row) =>
          row.empId ||
          row.emp_id ||
          row.EmpId ||
          row.employee_id ||
          row.id ||
          row.assignedId ||
          row.assigned_id ||
          "",
      },
      {
        key: "employee",
        label: "Employee Name",
        extract: (row) =>
          row.name ||
          row.employee_name ||
          `${row.first_name || ""} ${row.last_name || ""}`.trim() ||
          "",
      },
      {
        key: "designation",
        label: "Designation",
        extract: (row) =>
          row.designation ||
          row.designation_name ||
          row.job_title ||
          row.designationName ||
          "",
      },
      {
        key: "hq",
        label: "HQ",
        extract: (row) =>
          row.hq ||
          row.headquarter_name ||
          row.headquarter ||
          row.hq_name ||
          "",
      },
      {
        key: "division",
        label: "Division",
        extract: (row) => row.division || row.division_name || row.div || "",
      },
      {
        key: "doj",
        label: "DOJ",
        extract: (row) => {
          const val = row.doj || row.joining_date || row.date_of_joining || "";
          try {
            if (!val) return "";
            const d = new Date(val);
            if (!isNaN(d)) return format(d, "dd MMM yyyy");
            return String(val);
          } catch {
            return String(val);
          }
        },
      },

      {
        key: "manager",
        label: "Manager",
        extract: (row) =>
          row.manager ||
          row.reporting_manager ||
          row.manager_name ||
          row.reporting_to_name ||
          "",
      },
    ];
  }, []);

  const exportToCSV = () => {
    if (!tableData || tableData.length === 0) return;

    const csvRows = [];
    csvRows.push(columns.map((c) => `"${c.label}"`).join(","));

    tableData.forEach((row, rowIndex) => {
      const values = columns.map((c) => {
        const raw = c.extract(row, rowIndex);
        if (raw === null || raw === undefined) return '""';
        if (typeof raw === "object") {
          return `"${JSON.stringify(raw).replace(/"/g, '""')}"`;
        }
        return `"${String(raw).replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const fileName = `employee_daily_temptation_${format(
      selectedDate,
      "yyyyMMdd"
    )}.csv`;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    try {
      const printWindow = window.open("", "_blank");

      const tableHTML = `
        <table>
          <thead>
            <tr>
              ${columns.map((c) => `<th>${c.label}</th>`).join("")}
              <th>DCR</th>
            </tr>
          </thead>
          <tbody>
            ${tableData
              .map((row, rowIndex) => {
                const cells = columns
                  .map(
                    (c) => `<td>${String(c.extract(row, rowIndex) || "")}</td>`
                  )
                  .join("");
                const empId =
                  row.empId ||
                  row.emp_id ||
                  row.EmpId ||
                  row.employee_id ||
                  row.id ||
                  row.assignedId ||
                  row.assigned_id ||
                  "";
                const dcrRef = empId
                  ? `/api/employees/${encodeURIComponent(
                      empId
                    )}/visit-full-summary?date=${encodeURIComponent(
                      format(selectedDate, "yyyy-MM-dd")
                    )}`
                  : "N/A";
                return `<tr>${cells}<td>${dcrRef}</td></tr>`;
              })
              .join("")}
          </tbody>
        </table>
      `;

      const selectedStateName =
        states.find((s) => s.id == selectedState)?.name || "All";
      const selectedDivisionName =
        selectedDivision === "All"
          ? "All"
          : divisions.find((d) => d.name === selectedDivision)?.name || "N/A";

      printWindow.document.write(`
        <html>
          <head>
            <title>Employee Daily Temptation</title>
            <style>
              @page { size: landscape; margin: 10mm; }
              body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
              .print-header { margin-bottom: 15px; }
              .print-title { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
              .print-filters { font-size: 14px; margin-bottom: 15px; }
              table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 10px; }
              th, td { border: 1px solid #ddd; padding: 4px; text-align: center; }
              th { background-color: #f2f2f2; font-weight: bold; }
              tr { page-break-inside: avoid; }
            </style>
          </head>
          <body>
            <div class="print-header">
              <div class="print-title">Employee Daily Temptation Report</div>
              <div class="print-filters">
                <div>State: ${selectedStateName}</div>
                <div>Division: ${selectedDivisionName}</div>
                <div>Date: ${format(selectedDate, "dd MMM yyyy")}</div>
                <div>Generated on: ${new Date().toLocaleDateString()}</div>
              </div>
            </div>
            ${tableHTML}
            <script>
              window.onload = function() {
                setTimeout(() => window.print(), 400);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    } catch (error) {
      console.error("Error printing:", error);
      setError("Failed to generate print view");
    }
  };

  // Helper to choose emp id from possible keys
  const getEmployeeIdFromRow = (row) => {
    if (!row) return null;
    return (
      row.empId ||
      row.emp_id ||
      row.EmpId ||
      row.employee_id ||
      row.id ||
      row.assignedId ||
      row.assigned_id ||
      null
    );
  };

  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      {/* overlay loader */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <Loader />
        </div>
      )}
      <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">Campaign </span>
          <span className="text-blue-500">Daily</span>
        </h1>

        <div className="punchedAttendence-filter-row1">
          <label className="punchedAttendence-label">State</label>
          <select
            className="punchedAttendence-dropdown"
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            disabled={isLoading}
          >
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          <label className="punchedAttendence-label">Division</label>
          <select
            className="punchedAttendence-dropdown"
            value={selectedDivision}
            onChange={(e) => handleDivisionChange(e.target.value)}
            disabled={!selectedState || isLoading}
          >
            {divisions.map((division) => (
              <option key={division.id} value={division.name}>
                {division.name}
              </option>
            ))}
          </select>
        </div>

        <div className="punchedAttendence-filter-row2">
          <label className="punchedAttendence-label">Employee</label>
          <select
            className="punchedAttendence-dropdown"
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            disabled={!selectedState || isLoading}
          >
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.id === "All"
                  ? "All"
                  : `${emp.first_name || ""} ${emp.last_name || ""} ${
                      emp.designation ? `(${emp.designation})` : ""
                    }`.trim()}
              </option>
            ))}
          </select>

          <label className="punchedAttendence-label">Date</label>
          <input
            type="date"
            className="punchedAttendence-dropdown"
            value={format(selectedDate, "yyyy-MM-dd")}
            onChange={(e) =>
              setSelectedDate(e.target.value ? new Date(e.target.value) : null)
            }
            disabled={isLoading}
          />
        </div>

        {/* SUBMIT BUTTON */}
        <div className="flex justify-center mt-4">
          <CustomButton
            variant="go"
            onClick={handleGoClick}
            disabled={isLoading}
          >
            {isLoading ? <Loader /> : "GO"}
          </CustomButton>
        </div>
      </div>

      {showContainers && (
        <div className="p-2  border-2 border-gray-300 rounded-lg bg-white mt-5">
          <div className="text-center mb-2">
            <h2 className="text-lg font-semibold">
              Month Temptation ({format(selectedDate, "dd MMM yyyy")})
            </h2>
          </div>

          <div className="border rounded shadow  table-wrapper">
            <table className="unlock-dcr-leave-table text-sm ">
              <thead className="bg-blue-500 text-white">
                <tr>
                  {columns.map((col, idx) => (
                    <th
                      key={col.key + idx}
                      className="border border-gray-300 px-2 py-1 font-semibold text-center whitespace-normal break-words"
                    >
                      {col.label}
                    </th>
                  ))}
                  <th className="border border-gray-300 px-2 py-1 font-semibold text-center">
                    DCR
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableData.length === 0 ? (
                  <tr>
                    <td
                      className="border border-gray-300 px-2 py-3 text-center"
                      colSpan={columns.length + 1}
                    >
                      No records found
                    </td>
                  </tr>
                ) : (
                  tableData.map((row, i) => {
                    const empId = getEmployeeIdFromRow(row);
                    const dcrTo = empId
                      ? `/pobdatewisedetails?employee_id=${encodeURIComponent(
                          empId
                        )}&date=${encodeURIComponent(
                          format(selectedDate, "yyyy-MM-dd")
                        )}`
                      : null;

                    return (
                      <tr
                        key={i}
                        className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        {columns.map((col, cidx) => (
                          <td
                            key={col.key + "-" + cidx}
                            className="border border-gray-300 px-2 py-1 text-center align-top break-words"
                          >
                            {col.extract(row, i) !== null &&
                            col.extract(row, i) !== undefined
                              ? String(col.extract(row, i))
                              : ""}
                          </td>
                        ))}

                        <td className="border border-gray-300 px-2 py-1 text-center">
                          {dcrTo ? (
                            <Link
                              to={dcrTo}
                              className="text-blue-600 underline hover:text-blue-800"
                            >
                              View
                            </Link>
                          ) : (
                            "N/A"
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex gap-4 justify-center">
            <CustomButton onClick={handlePrint} label="Print" variant="print">
              🖨
            </CustomButton>
            <CustomButton onClick={exportToCSV} label="Export" variant="export">
              📥
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCampaignDailyReport;
